/* eslint-disable @typescript-eslint/no-explicit-any */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Controller, useForm, type FieldErrors } from 'react-hook-form';
import { z } from 'zod';
import { Chips } from 'primereact/chips';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { Input } from 'sharedUi/Input';
import { TextArea } from 'sharedUi/TextArea';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { useNavigationBlocker } from '../../hooks/useNavigationBlocker';
import DiscardChangesDialog from '../../components/dialogs/discard-changes-dialog';
import FormSelect from '../../components/forms/form-select';
import { useCanUpdate } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import { knowledgeBaseService, type KbUpsertRequest } from '../../services/knowledge-base.service';
import { projectsService } from '../../services/projects.service';
import { extractErrorMessage } from '../../utils/error-handler';
import { PageSkeleton, SectionLabel } from '../../components/common/panel';
import ScreenHeader from '../../components/common/screen-header';
import { PANEL, PRETTY } from '../../components/common/ui-kit';
import { sourceKindLabel } from './knowledge-base-status';

/** Only `kbCode` and `name` are required. Enum membership is NOT validated here —
 *  the backend checks it against the KB value sets so an unknown value returns the
 *  domain's own sentence naming the allowed ones, instead of a Pydantic complaint
 *  about a Literal the operator never saw.
 *
 *  No classification or reader list: access is the `KNOWLEDGE_BASE.*` RBAC keys, and
 *  a policy field here that retrieval did not read would describe a restriction the
 *  system does not apply. */
const schema = z.object({
  kbCode: z.string().min(1, 'A code is required').max(150).trim(),
  name: z.string().min(1, 'A name is required').max(200).trim(),
  description: z.string().optional().or(z.literal('')),
  appId: z.string().optional().or(z.literal('')),
  tags: z.array(z.string()),
  sourceKind: z.string(),
  suggestedChunkingPreset: z.string().optional().or(z.literal('')),
  status: z.string(),
});
type FormData = z.infer<typeof schema>;

const KnowledgeBaseForm = () => {
  const { id, isReady, navigateToView } = useSecureNavigation({ entity: 'knowledge-base', basePath: '/knowledge-bases' });
  const isEditMode = Boolean(id);
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);
  const canEdit = useCanUpdate(JOB_NAMES.KNOWLEDGE_BASE);

  const { control, handleSubmit, reset, watch, setValue, formState: { errors, isDirty } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      kbCode: '', name: '', description: '', appId: '',
      tags: [], sourceKind: 'DOCUMENTS', suggestedChunkingPreset: '', status: 'ACTIVE',
    },
    mode: 'onChange',
  });

  /** The vocabularies the VALIDATOR accepts, from the backend. A hand-written copy
   *  here would be a dropdown that can offer a value the save rejects. */
  const { data: options } = useQuery({
    queryKey: ['kb-options'], queryFn: knowledgeBaseService.options, staleTime: Infinity,
  });

  const { data: accessibleProjects = [], isLoading: projectsLoading } = useQuery({
    queryKey: ['projects', 'accessible'], queryFn: projectsService.getAccessible, staleTime: 5 * 60 * 1000,
  });
  const projectOptions = accessibleProjects.map((p) => ({ value: p._id, label: p.appCode ? `${p.name} (${p.appCode})` : p.name }));
  const projectLocked = accessibleProjects.length <= 1;
  const selectedAppId = watch('appId');
  useEffect(() => { if (!isEditMode && !selectedAppId && accessibleProjects.length) setValue('appId', accessibleProjects[0]._id); }, [isEditMode, selectedAppId, accessibleProjects, setValue]);

  const { data: existing, isLoading } = useQuery({
    queryKey: ['knowledge-bases', id],
    queryFn: () => knowledgeBaseService.getById(id!),
    enabled: isReady && !!id,
  });
  useBreadcrumbTitle(existing?.name);

  useEffect(() => {
    if (!existing) return;
    reset({
      kbCode: existing.kbCode || '',
      name: existing.name || '',
      description: existing.description || '',
      appId: existing.appId || '',
      tags: existing.tags || [],
      sourceKind: existing.sourceKind || 'DOCUMENTS',
      suggestedChunkingPreset: existing.suggestedChunkingPreset || '',
      status: existing.status || 'ACTIVE',
    });
  }, [existing, reset]);

  const { showLeaveDialog, confirmLeave, cancelLeave, handleNavigate } = useNavigationBlocker({ when: isDirty });

  const mutation = useMutation({
    mutationFn: (payload: KbUpsertRequest) =>
      isEditMode ? knowledgeBaseService.update(id!, payload) : knowledgeBaseService.create(payload),
    onSuccess: (saved: any) => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-bases'] });
      toast.current?.show({ severity: 'success', summary: 'Saved',
                            detail: `Knowledge base ${isEditMode ? 'updated' : 'created'}`, life: 3000 });
      // Land on the knowledge base itself, on Documents. Returning to the list left
      // a brand-new, empty corpus with no visible way to put anything in it.
      const savedId = saved?._id || id;
      if (savedId) setTimeout(() => navigateToView(savedId, isEditMode ? '' : '?tab=documents'), 800);
    },
    onError: (error: any) => toast.current?.show({
      severity: 'error', summary: 'Error',
      detail: extractErrorMessage(error, 'Failed to save the knowledge base'), life: 10000,
    }),
  });

  const onSubmit = (d: FormData) => {
    const payload: KbUpsertRequest = {
      kbCode: d.kbCode, name: d.name, description: d.description || null,
      tags: d.tags,
      sourceKind: d.sourceKind,
      // Always sent, empty included: an absent key means "leave it alone", so
      // omitting a cleared field would hand the old value back.
      suggestedChunkingPreset: d.suggestedChunkingPreset || null,
      status: d.status,
    };
    if (!isEditMode && d.appId) payload.appId = d.appId;
    mutation.mutate(payload);
  };

  /** Submitting with an invalid field must not fail silently. Say what is wrong and
   *  bring it into view. */
  const onInvalid = (formErrors: FieldErrors<FormData>) => {
    const first = Object.keys(formErrors)[0] as keyof FormData | undefined;
    if (!first) return;
    toast.current?.show({
      severity: 'warn', summary: 'Check the form',
      detail: (formErrors[first] as any)?.message || 'One of the fields needs attention',
      life: 6000,
    });
    document.querySelector(`[name="${first}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const sourceKindOptions = (options?.sourceKinds || ['DOCUMENTS', 'URLS', 'MIXED'])
    .map((k) => ({ value: k, label: sourceKindLabel(k) }));
  const presetOptions = [{ value: '', label: 'No suggestion' },
    ...(options?.chunkingPresets || []).map((p) => ({ value: p.code, label: p.label }))];

  if (isEditMode && isLoading) return <PageSkeleton panels={3} />;

  const card = `${PANEL} p-4`;

  return (
    <div className="py-4 px-4 lg:px-6 bg-canvas dark:bg-gray-900" data-testid="SCR-KnowledgeBase-Form">
      <Toast ref={toast} position="top-right" />
      <ScreenHeader
        icon="pi pi-book"
        onBack={() => handleNavigate('/knowledge-bases')}
        title={isEditMode ? existing?.name || 'Knowledge base' : 'New knowledge base'}
        right={isEditMode ? existing?.kbCode : undefined}
      />

      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="flex w-full flex-col gap-4">
        {/* ── Identity ────────────────────────────────────────────────── */}
        <div className={card}>
          <SectionLabel icon="pi pi-tag" className="mb-2.5">Identity</SectionLabel>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Controller name="kbCode" control={control} render={({ field }) => (
              <Input label="Code *" {...field} error={errors.kbCode?.message}
                     placeholder="KB.Legal.CriminalLaw" disabled={isEditMode} />
            )} />
            <Controller name="name" control={control} render={({ field }) => (
              <Input label="Name *" {...field} error={errors.name?.message} />
            )} />
            <Controller name="status" control={control} render={({ field }) => (
              <FormSelect label="Status" name={field.name} value={field.value || 'ACTIVE'}
                          options={[{ label: 'Active', value: 'ACTIVE' }, { label: 'Inactive', value: 'INACTIVE' }]}
                          onChange={field.onChange} />
            )} />
            {!isEditMode && (
              <Controller name="appId" control={control} render={({ field }) => (
                <FormSelect label="Project" name={field.name} value={field.value || ''}
                            options={projectOptions} onChange={field.onChange} disabled={projectLocked} />
              )} />
            )}
            <Controller name="description" control={control} render={({ field }) => (
              <div className="sm:col-span-2"><TextArea label="Description" {...field} rows={2} /></div>
            )} />
          </div>
        </div>

        {/* ── Labels ──────────────────────────────────────────────────── */}
        <div className={card}>
          <SectionLabel icon="pi pi-tags" className="mb-2.5">Labels</SectionLabel>
          <Controller name="tags" control={control} render={({ field }) => (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Tags</label>
              <Chips name={field.name} value={field.value} onChange={(e) => field.onChange(e.value || [])}
                     placeholder="Domain, language, jurisdiction — Enter after each"
                     className="w-full" aria-label="Tags" />
              <span className="text-xs leading-relaxed text-ink-muted" style={PRETTY}>
                Searchable alongside the code and name. Who may reach this corpus is decided
                by role permissions, not by anything set here.
              </span>
            </div>
          )} />
        </div>

        {/* ── Content ─────────────────────────────────────────────────── */}
        <div className={card}>
          <SectionLabel icon="pi pi-file" className="mb-2.5">Content</SectionLabel>
          <div className="flex flex-col gap-3">
            <Controller name="sourceKind" control={control} render={({ field }) => (
              <FormSelect label="What this corpus is made of" name={field.name}
                          value={field.value || 'DOCUMENTS'} options={sourceKindOptions}
                          onChange={field.onChange} />
            )} />
            <Controller name="suggestedChunkingPreset" control={control} render={({ field }) => (
              <div className="flex flex-col gap-1">
                <FormSelect label="Suggested chunking" name={field.name} value={field.value || ''}
                            options={presetOptions} onChange={field.onChange} />
                <span className="text-xs leading-relaxed text-ink-muted" style={PRETTY}>
                  A hint the pipeline form starts from, not a rule. Chunking belongs to the
                  pipeline because the pipeline is the thing that can be re-run — and one
                  corpus indexed two ways is the point of keeping them separate.
                </span>
              </div>
            )} />
          </div>
        </div>

        <div className="flex justify-end gap-2 pb-2">
          <Button type="button" label="Cancel" severity="secondary" outlined
                  onClick={() => handleNavigate('/knowledge-bases')}
                  disabled={mutation.isPending} data-testid="KnowledgeBaseForm.Button.Cancel" />
          <span className="ui-press inline-flex">
            <Button type="submit"
                    label={mutation.isPending ? 'Saving…' : isEditMode ? 'Save changes' : 'Create'}
                    icon={mutation.isPending ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                    // Until the projects arrive the Project field is empty, and a
                    // create sent then lands in the caller's home project — not the one
                    // the form goes on to show as selected.
                    disabled={mutation.isPending || (isEditMode && !canEdit) || (!isEditMode && projectsLoading)}
                    data-testid="KnowledgeBaseForm.Button.Submit" />
          </span>
        </div>
      </form>

      <DiscardChangesDialog visible={showLeaveDialog} onStay={cancelLeave} onLeave={confirmLeave}
                            testId="KnowledgeBaseForm.Dialog.Leave" />
    </div>
  );
};

export default KnowledgeBaseForm;
